import {useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {toast} from "sonner";
import Nav from "../../components/ui/nav/nav";
import ConfirmDeletePopup from "../../components/popups/confirmDeletePopup";
import ConstructorService from "../../service/constructorService";
import {API_URL} from "../../http";
import catalogStyle from "../catalogs/popup/createCatalog.module.scss";
import productPopupStyle from "../products/popups/productPopup.module.scss";
import style from "./constructorPage.module.scss";

const fieldsByType = {
    product: [
        {key: "title", label: "Название", required: true},
        {key: "cost", label: "Цена", type: "number", required: true},
        {key: "description", label: "Описание", type: "textarea"},
        {key: "weight", label: "Вес"}
    ],
    group: [
        {key: "title", label: "Название группы", required: true},
        {key: "qtyMin", label: "Минимальное количество", type: "number"},
        {key: "qtyMax", label: "Максимальное количество", type: "number"},
        {key: "weight", label: "Вес"},
        {key: "default", label: "Выбрана по умолчанию", type: "checkbox"}
    ],
    item: [
        {key: "title", label: "Название товара", required: true},
        {key: "cost", label: "Цена", type: "number", required: true},
        {key: "weight", label: "Вес"},
        {key: "qtyMax", label: "Максимальное количество", type: "number"}
    ]
};

const initialValues = type => type === "product"
    ? {title: "", cost: "", description: "", weight: ""}
    : type === "group"
        ? {title: "", qtyMin: 1, qtyMax: 0, weight: "", default: false}
        : {title: "", cost: "", weight: "", qtyMax: 0, image: ""};

const getConstructorImageUrl = image => {
    if (!image) return null;
    if (/^(https?:|data:)/i.test(image)) return image;
    if (image.startsWith("/")) return `${API_URL}${image}`;
    if (image.includes("/")) return `${API_URL}/${image}`;
    return `${API_URL}/img/constructor/${image}`;
};

const saveConstructorEntity = async ({type, id, parentId, data}) => {
    if (type === "product") {
        return ConstructorService.updateProduct(id, data);
    }
    if (type === "group") {
        return id ? ConstructorService.updateGroup(id, data) : ConstructorService.createGroup(parentId, data);
    }
    return id ? ConstructorService.updateItem(id, data) : ConstructorService.createItem(parentId, data);
};

const deleteConstructorEntity = ({type, id}) => {
    if (type === "product") return ConstructorService.deleteProduct(id);
    if (type === "group") return ConstructorService.deleteGroup(id);
    return ConstructorService.deleteItem(id);
};

const EntityForm = ({form, setForm, onClose, onSubmit, isSaving}) => {
    const fields = fieldsByType[form.type];
    const imageInputRef = useRef(null);
    const imagePreview = form.imagePreview || getConstructorImageUrl(form.values.image);
    const handleImageChange = event => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            event.target.value = "";
            alert("Пожалуйста, выберите изображение.");
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => setForm({...form, imageFile: file, imagePreview: reader.result});
        reader.readAsDataURL(file);
    };
    const removeSelectedImage = () => {
        setForm({...form, imageFile: null, imagePreview: null});
        if (imageInputRef.current) imageInputRef.current.value = "";
    };
    return (
        <div className={style.overlay} onMouseDown={event => {
            if (event.target === event.currentTarget) onClose();
        }}>
            <form className={style.dialog} onSubmit={onSubmit}>
                <div className={style.dialogTitle}>
                    {form.id ? "Редактирование" : "Добавление"}: {form.type === "product" ? "товара конструктора" : form.type === "group" ? "группы" : "товара группы"}
                </div>
                {fields.map(field => (
                    <label className={style.field} key={field.key}>
                        {field.type === "checkbox" ? (
                            <>
                                <input
                                    type="checkbox"
                                    checked={Boolean(form.values[field.key])}
                                    onChange={event => setForm({...form, values: {...form.values, [field.key]: event.target.checked}})}
                                />
                                <span>{field.label}</span>
                            </>
                        ) : (
                            <>
                                <span>{field.label}</span>
                                {field.type === "textarea" ? (
                                    <textarea
                                        value={form.values[field.key]}
                                        required={field.required}
                                        onChange={event => setForm({...form, values: {...form.values, [field.key]: event.target.value}})}
                                    />
                                ) : (
                                    <input
                                        type={field.type || "text"}
                                        min={field.type === "number" ? 0 : undefined}
                                        step={field.key === "cost" ? 1 : "any"}
                                        value={form.values[field.key] ?? ""}
                                        required={field.required}
                                        onChange={event => setForm({...form, values: {...form.values, [field.key]: event.target.value}})}
                                    />
                                )}
                            </>
                        )}
                    </label>
                ))}
                {form.type === "item" && (
                    <div className={[catalogStyle.photo, style.itemPhoto].join(" ")}>
                        <div>
                            <div className={catalogStyle.label}>Фото товара</div>
                            <div className={[catalogStyle.description, style.itemPhotoDescription].join(" ")}>
                                Изображение автоматически кадрируется в соотношении 30:21.
                            </div>
                            <input
                                ref={imageInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className={catalogStyle.hiddenInput}
                            />
                            <button
                                type="button"
                                onClick={() => imageInputRef.current?.click()}
                                className={catalogStyle.uploadButton}
                            >
                                <span className={catalogStyle.plus}>＋</span>
                                {form.values.image || form.imagePreview ? "Изменить фото" : "Загрузить фото"}
                            </button>
                        </div>
                        {imagePreview && (
                            <div className={[catalogStyle.previewContainer, style.itemPhotoPreview].join(" ")}>
                                <div className={productPopupStyle.productPreviewFrame}>
                                    <img
                                        src={imagePreview}
                                        alt="Предпросмотр изображения товара"
                                        className={productPopupStyle.productPreviewImage}
                                    />
                                </div>
                                {form.imagePreview && (
                                    <button
                                        type="button"
                                        className={catalogStyle.deleteImage}
                                        onClick={removeSelectedImage}
                                        aria-label="Удалить выбранное фото"
                                    >
                                        <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M16.8037 6.19531L6.19711 16.8019" stroke="#CE1919" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M16.8037 16.8027L6.19711 6.19613" stroke="#CE1919" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <div className={style.formActions}>
                    <button type="button" className={style.secondaryButton} onClick={onClose}>Отмена</button>
                    <button type="submit" className={style.primaryButton} disabled={isSaving}>
                        {isSaving ? "Сохранение..." : "Сохранить"}
                    </button>
                </div>
            </form>
        </div>
    );
};

const ConstructorPage = () => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const {data: products = [], isPending, isError} = useQuery({
        queryKey: ["constructorProducts"],
        queryFn: () => ConstructorService.getProducts()
    });
    const saveMutation = useMutation({
        mutationFn: saveConstructorEntity,
        onSuccess: () => {
            toast.success("Изменения сохранены");
            setForm(null);
            queryClient.invalidateQueries({queryKey: ["constructorProducts"]});
        },
        onError: () => toast.error("Не удалось сохранить изменения")
    });
    const deleteMutation = useMutation({
        mutationFn: deleteConstructorEntity,
        onSuccess: () => {
            toast.success("Удалено");
            setDeleteTarget(null);
            queryClient.invalidateQueries({queryKey: ["constructorProducts"]});
        },
        onError: () => toast.error("Не удалось удалить")
    });

    const openForm = (type, parentId, entity) => setForm({
        type,
        parentId,
        id: entity?.id,
        values: entity ? {...initialValues(type), ...entity} : initialValues(type)
    });
    const onSubmit = event => {
        event.preventDefault();
        const data = {...form.values};
        ["cost", "qtyMin", "qtyMax"].forEach(key => {
            if (data[key] !== undefined && data[key] !== "") data[key] = Number(data[key]);
        });
        if (form.type === "item") {
            delete data.image;
            const formData = new FormData();
            Object.entries(data).forEach(([key, value]) => formData.append(key, value));
            if (form.imageFile) formData.append("file", form.imageFile);
            saveMutation.mutate({type: form.type, id: form.id, parentId: form.parentId, data: formData});
            return;
        }
        saveMutation.mutate({type: form.type, id: form.id, parentId: form.parentId, data});
    };

    return (
        <div className="page">
            <div className="page_container">
                <Nav title="Конструктор">
                    <main className={style.body}>
                        <div className={style.toolbar}>
                            <div>
                                <h1 className={style.heading}>Конструктор блюд</h1>
                                <p className={style.subtitle}>Управление товарами, группами и наполнением конструктора</p>
                            </div>
                        </div>
                        {isPending && <div className={style.state}>Загрузка конструктора...</div>}
                        {isError && <div className={style.state}>Не удалось загрузить товары конструктора.</div>}
                        {!isPending && !isError && products.length === 0 && (
                            <div className={style.state}>Товаров конструктора пока нет.</div>
                        )}
                        <div className={style.productList}>
                            {products.map(product => (
                                <section className={style.productCard} key={product.id}>
                                    <header className={style.productHeader}>
                                        <div>
                                            <h2>{product.title}</h2>
                                            <span>{product.cost ?? 0} ₽{product.weight ? ` · ${product.weight}` : ""}</span>
                                        </div>
                                        <div className={style.actions}>
                                            <button onClick={() => openForm("product", null, product)}>Редактировать</button>
                                            <button className={style.deleteAction} onClick={() => setDeleteTarget({
                                                type: "product", id: product.id, title: product.title, label: "товар конструктора"
                                            })}>Удалить</button>
                                            <button className={style.primaryButton} onClick={() => openForm("group", product.id)}>Добавить группу</button>
                                        </div>
                                    </header>
                                    {product.description && <p className={style.productDescription}>{product.description}</p>}
                                    <div className={style.groups}>
                                        {(product.groups || []).map(group => (
                                            <article className={style.groupCard} key={group.id}>
                                                <header className={style.groupHeader}>
                                                    <div>
                                                        <h3>{group.title}</h3>
                                                        <span>
                                                            Выбор: {group.qtyMin}–{group.qtyMax || "без лимита"}
                                                            {group.default ? " · По умолчанию" : ""}
                                                            {group.weight ? ` · ${group.weight}` : ""}
                                                        </span>
                                                    </div>
                                                    <div className={style.actions}>
                                                        <button onClick={() => openForm("group", product.id, group)}>Изменить</button>
                                                        <button className={style.deleteAction} onClick={() => setDeleteTarget({
                                                            type: "group", id: group.id, title: group.title, label: "группу"
                                                        })}>Удалить</button>
                                                        <button className={style.secondaryButton} onClick={() => openForm("item", group.id)}>Добавить товар</button>
                                                    </div>
                                                </header>
                                                <div className={style.items}>
                                                    {(group.items || []).map(item => (
                                                        <div className={style.itemRow} key={item.id}>
                                                            <div className={style.itemThumbnail}>
                                                                {item.image && (
                                                                    <img
                                                                        src={getConstructorImageUrl(item.image)}
                                                                        alt={item.title}
                                                                    />
                                                                )}
                                                            </div>
                                                            <div className={style.itemInfo}>
                                                                <strong>{item.title}</strong>
                                                                <span>{item.cost} ₽{item.weight ? ` · ${item.weight}` : ""}{item.qtyMax ? ` · макс. ${item.qtyMax}` : ""}</span>
                                                            </div>
                                                            <div className={style.actions}>
                                                                <button onClick={() => openForm("item", group.id, item)}>Изменить</button>
                                                                <button className={style.deleteAction} onClick={() => setDeleteTarget({
                                                                    type: "item", id: item.id, title: item.title, label: "товар группы"
                                                                })}>Удалить</button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {!group.items?.length && <div className={style.emptyItems}>В группе пока нет товаров.</div>}
                                                </div>
                                            </article>
                                        ))}
                                        {!product.groups?.length && <div className={style.emptyItems}>У товара пока нет групп.</div>}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </main>
                </Nav>
            </div>
            {form && (
                <EntityForm
                    form={form}
                    setForm={setForm}
                    onClose={() => setForm(null)}
                    onSubmit={onSubmit}
                    isSaving={saveMutation.isPending}
                />
            )}
            <ConfirmDeletePopup
                active={deleteTarget && {
                    title: `Удалить ${deleteTarget.label}?`,
                    message: `«${deleteTarget.title}» будет удален без возможности восстановления.`
                }}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={() => deleteMutation.mutate({type: deleteTarget.type, id: deleteTarget.id})}
            />
        </div>
    );
};

export default ConstructorPage;
